import { getPref, setPref } from '../persistentPrefs';
import {
  spendSkillPoint as spendSkillPointApi,
  setSkillElement as setSkillElementApi,
  purchaseSkillResetServer,
} from './profileApi';
import { k } from '../../localization/t';

const STORAGE_KEY = 'vh-skills-v1';

// Persistent state shape:
//   { pointsSpent: ['merchant_1', 'artisan_3', ...], hunterElement }
// The server is the source of truth. Skill-gated mechanics like
// Auction House listing capacity, fee tier, and timed bidding
// auctions all read the stored blob server-side. The local cache
// here exists so renders are sync and fast; it gets seeded from
// arenaProfile.skills on profile load and updated alongside every
// successful server-side mutation.

const DEFAULT_STATE = Object.freeze({
  pointsSpent: [],
  hunterElement: null,
});

export function getSkillState() {
  const raw = getPref(STORAGE_KEY, null);
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_STATE, pointsSpent: [] };
  return {
    pointsSpent: Array.isArray(raw.pointsSpent) ? [...raw.pointsSpent] : [],
    hunterElement: typeof raw.hunterElement === 'string' ? raw.hunterElement : null,
  };
}

function saveState(state) {
  setPref(STORAGE_KEY, {
    pointsSpent: Array.isArray(state?.pointsSpent) ? state.pointsSpent : [],
    hunterElement: typeof state?.hunterElement === 'string' ? state.hunterElement : null,
  });
}

// Replace the local cache with whatever the server returned. Called
// from app.jsx whenever loadArenaProfile resolves so the cached
// state and the server stay in lockstep.
export function seedSkillState(skills) {
  if (skills && typeof skills === 'object') saveState(skills);
}

// Optimistically update the local cache and push the spend to the
// server. Rolls the cache back on failure so a tampered or rejected
// spend never lingers locally. Returns the server-returned skills
// object on success.
export async function spendPoint(nodeKey) {
  const before = getSkillState();
  if (before.pointsSpent.includes(nodeKey)) return before;
  const optimistic = {
    ...before,
    pointsSpent: [...before.pointsSpent, nodeKey],
  };
  saveState(optimistic);
  try {
    const res = await spendSkillPointApi(nodeKey);
    const next = res?.skills || optimistic;
    saveState(next);
    return next;
  } catch (err) {
    saveState(before);
    throw err;
  }
}

export async function setHunterElement(element) {
  const before = getSkillState();
  if (before.hunterElement) return before;
  const optimistic = { ...before, hunterElement: element || null };
  saveState(optimistic);
  try {
    const res = await setSkillElementApi(element);
    const next = res?.skills || optimistic;
    saveState(next);
    return next;
  } catch (err) {
    saveState(before);
    throw err;
  }
}

// Gold price of a respec. Mirrors SKILL_RESET_COST in
// valkenhall-server/src/utils/skills.js, which is the value actually
// charged. This copy only drives the button label and the local
// affordability check.
export const SKILL_RESET_COST = 500;

// Player-facing respec. Charges gold server-side and hands every spent
// point back. No optimistic write: gold is on the line, so the local
// cache only moves once the server has committed the charge. Throws
// ApiError (402 when the player can't afford it, 400 when there's
// nothing spent) and leaves the cache untouched on failure.
export async function purchaseSkillReset() {
  const res = await purchaseSkillResetServer();
  const next = res?.skills || DEFAULT_STATE;
  saveState(next);
  return next;
}

// Skill points are earned every two player levels. With a level cap
// of 30 that's 15 points total. The tree has 30 nodes, so a fully-
// levelled player can fill exactly half of it. This forces real
// specialization: you cannot max every path, you have to commit to
// 2–3 of them, so every point is a real decision. Reversing one is
// deliberately expensive rather than impossible: purchaseSkillReset
// wipes the whole tree for SKILL_RESET_COST gold.
export const TOTAL_SKILL_POINTS = 15;

// Available skill points come from the server-side granted_skill_points
// column, surfaced on the arenaProfile as `grantedSkillPoints` (top-
// level) or `progression.grantedSkillPoints`. The Progression screen
// is the sole path that advances this counter. The player must claim
// each SP cell explicitly. Pass an arenaProfile snapshot or the
// granted-count directly.
export function getAvailableSkillPoints(profileOrGrantedCount) {
  if (typeof profileOrGrantedCount === 'number') {
    return Math.min(Math.max(0, Math.floor(profileOrGrantedCount)), TOTAL_SKILL_POINTS);
  }
  const profile = profileOrGrantedCount;
  const granted = profile?.progression?.grantedSkillPoints
    ?? profile?.grantedSkillPoints
    ?? 0;
  return Math.min(Math.max(0, granted), TOTAL_SKILL_POINTS);
}

// ── Path / node definitions ─────────────────────────────────────
// Each path has 6 nodes (5 numeric + 1 capstone). All nodes cost 1
// point. Investment is sequential within a path. A node is only
// available when the previous node in its path has been purchased.

export const SKILL_PATHS = [
  {
    key: 'merchant',
    title: 'Reliquarian',
    tagline: 'Trader of Relics, Master of Coin',
    accent: '#e8c87a',
    nodes: [
      { key: 'merchant_1', title: 'Apprentice Trader',   desc: 'Auction House listing capacity raised from 3 to 5 simultaneous listings.' },
      { key: 'merchant_2', title: 'Guild Concession',    desc: 'Auction House listing fee reduced from 30% to 15% of the sale price.' },
      { key: 'merchant_3', title: 'Established Trader',  desc: 'Auction House listing capacity raised from 5 to 10 simultaneous listings.' },
      { key: 'merchant_4', title: 'Royal Warrant',       desc: 'Auction House listing fee reduced from 15% to 5% of the sale price.' },
      { key: 'merchant_5', title: 'Merchant Prince',     desc: 'Auction House listing capacity raised from 10 to 20 simultaneous listings.' },
      { key: 'merchant_6', title: 'Auctioneer Arcane',   desc: 'Unlock timed bidding auctions in the Auction House as an alternative to fixed-price listings.', capstone: true },
    ],
  },
  {
    key: 'spellcraft',
    title: 'Inscription',
    tagline: 'Bind enchantment into rune and parchment',
    accent: '#e08055',
    nodes: [
      { key: 'spellcraft_1', title: 'Apprentice Scribe',  desc: '−5% Arcana Shard cost when crafting cards.' },
      { key: 'spellcraft_2', title: 'Journeyman Scribe',  desc: '−10% Arcana Shard cost when crafting cards.' },
      { key: 'spellcraft_3', title: 'Master Scribe',      desc: '−15% Arcana Shard cost when crafting cards.' },
      { key: 'spellcraft_4', title: 'Grand Scribe',       desc: '−20% Arcana Shard cost when crafting cards.' },
      { key: 'spellcraft_5', title: 'Inscriber of Wonders', desc: 'Unlock the ability to craft Unique-rarity cards in the Spellcraft interface.' },
      { key: 'spellcraft_6', title: 'Disenchant',         desc: 'Unlock the ability to disenchant duplicate cards for Arcana Shards.', capstone: true },
    ],
  },
  {
    key: 'scholar',
    title: 'Battle Mage',
    tagline: 'Power forged through endless trial',
    accent: '#7da9d4',
    nodes: [
      { key: 'scholar_1', title: 'Battle Initiate', desc: '+10% Season Pass XP from matches.' },
      { key: 'scholar_2', title: 'Trial Veteran',   desc: '+10% coin and Arcana Shard rewards from Arcane Trials tiers.' },
      { key: 'scholar_3', title: 'Battle Adept',    desc: '+20% Season Pass XP from matches (cumulative).' },
      { key: 'scholar_4', title: 'Trial Master',    desc: '+20% coin and Arcana Shard rewards from Arcane Trials tiers (cumulative).' },
      { key: 'scholar_5', title: 'Doubled Resolve', desc: 'Active quest slots increased from 3 to 6 for the season.' },
      { key: 'scholar_6', title: 'Archmage',        desc: 'Tier 9 of every Arcane Trials cycle awards an additional Elite foil card on top of its 200-coin reward.', capstone: true },
    ],
  },
  {
    key: 'hunter',
    title: 'Augmentation',
    tagline: 'Augment your spells with elemental favour',
    accent: '#9bbf7d',
    needsElement: true,
    nodes: [
      { key: 'hunter_1', title: 'Glint',          desc: '+5% foil chance for cards of your chosen element.' },
      { key: 'hunter_2', title: 'Glimmer',        desc: '+10% foil chance for cards of your chosen element.' },
      { key: 'hunter_3', title: 'Lustre',         desc: '+15% foil chance for cards of your chosen element.' },
      { key: 'hunter_4', title: 'Radiance',       desc: '+20% foil chance for cards of your chosen element.' },
      { key: 'hunter_5', title: 'Brilliance',     desc: '+25% foil chance for cards of your chosen element.' },
      { key: 'hunter_6', title: 'Fateful Resonance', desc: 'Whenever a booster pack rolls a foil card, there is a 10% chance another random card in the same pack also upgrades to foil.', capstone: true },
    ],
  },
  {
    key: 'transmuter',
    title: 'Transmutation',
    tagline: 'Reshape the very weave of reality',
    accent: '#c499d4',
    nodes: [
      { key: 'transmuter_1', title: 'Quickening',        desc: 'The Elite slot in every booster pack has a +5% chance to roll as a Unique instead.' },
      { key: 'transmuter_2', title: 'Quicksilver Touch', desc: 'Re-roll one Ordinary card per booster pack.' },
      { key: 'transmuter_3', title: 'Argent Reshape',    desc: 'Re-roll one Exceptional card per booster pack.' },
      { key: 'transmuter_4', title: 'Gilded Reshape',    desc: 'Re-roll one Elite card per booster pack.' },
      { key: 'transmuter_5', title: 'Sovereign Reshape', desc: 'Re-roll one Unique card per booster pack.' },
      { key: 'transmuter_6', title: 'Transmute',         desc: 'Once per booster pack, upgrade any single card up one rarity tier.', capstone: true },
    ],
  },
];

// The four elements the Hunter path's foil affinity can target.
export const HUNTER_ELEMENTS = [
  { key: 'air',   label: k('Air') },
  { key: 'earth', label: k('Earth') },
  { key: 'fire',  label: k('Fire') },
  { key: 'water', label: k('Water') },
];

export function getPathByKey(key) {
  return SKILL_PATHS.find((p) => p.key === key) || null;
}

// Per-node minimum player level. The third skill in each path opens
// at level 10, the capstone at level 20. Gates that ride alongside
// the existing prereq-chain rules. Returns 0 (no gate) for nodes
// without a level requirement.
export function getNodeMinLevel(nodeKey) {
  if (!nodeKey) return 0;
  if (nodeKey.endsWith('_6')) return 20;
  if (nodeKey.endsWith('_3')) return 10;
  return 0;
}

export function getNodeStatus(nodeKey, state, playerLevel = Infinity) {
  const path = SKILL_PATHS.find((p) => p.nodes.some((n) => n.key === nodeKey));
  if (!path) return 'locked';
  const idx = path.nodes.findIndex((n) => n.key === nodeKey);
  if (state.pointsSpent.includes(nodeKey)) return 'purchased';
  const prereqMet = idx === 0 || state.pointsSpent.includes(path.nodes[idx - 1].key);
  if (!prereqMet) return 'locked';
  if (playerLevel < getNodeMinLevel(nodeKey)) return 'locked';
  return 'available';
}

export function countSpent(state) {
  return state.pointsSpent.length;
}

// Path nodes have a hard prereq: Hunter requires a chosen element
// before any point can be spent in the path. Returns null if
// eligible, or a string describing the missing choice.
export function getInvestmentBlocker(pathKey, state) {
  const path = getPathByKey(pathKey);
  if (!path) return null;
  if (path.needsElement && !state.hunterElement) return 'pick-element';
  return null;
}

// ── Reliquarian (merchant path) effective benefits ────────────────
// These mirror src/utils/skills.js on the server. The server is the
// source of truth. It enforces these rates on /auction/list and
// /auction/buy. The client uses these helpers purely to preview the
// numbers in the UI (fee badge, "X / N listings" indicator).

export function getMerchantListingCapacity(state) {
  const spent = state?.pointsSpent || [];
  if (spent.includes('merchant_5')) return 20;
  if (spent.includes('merchant_3')) return 10;
  if (spent.includes('merchant_1')) return 5;
  return 3;
}

export function getMerchantListingFeePercent(state) {
  const spent = state?.pointsSpent || [];
  if (spent.includes('merchant_4')) return 5;
  if (spent.includes('merchant_2')) return 15;
  return 30;
}

export function canCreateTimedAuctions(state) {
  return Boolean(state?.pointsSpent?.includes('merchant_6'));
}

// Mirror of valkenhall-server/src/utils/skills.js#computeAuctionFee.
// The fee is taken out of the seller's payout; the buyer pays the
// sticker price. 1-coin sales are exempt because we cannot pay out
// fractional coins. Returns { tax, proceeds } so the UI preview and
// the server settlement use the same rounding rule.
export function computeAuctionFee(salePrice, feePercent) {
  const price = Math.max(0, Math.floor(salePrice || 0));
  if (price <= 1) return { tax: 0, proceeds: price };
  const tax = Math.floor(price * (feePercent || 0) / 100);
  return { tax, proceeds: price - tax };
}

// ── Inscription (spellcraft path) effective benefits ──────────────
// Mirrors valkenhall-server/src/utils/skills.js. Server enforces;
// client uses these for UI previews (price strikethroughs, Unique
// craft unlock, Disenchant button visibility).

export function getInscriptionDiscountPercent(state) {
  const spent = state?.pointsSpent || [];
  if (spent.includes('spellcraft_4')) return 20;
  if (spent.includes('spellcraft_3')) return 15;
  if (spent.includes('spellcraft_2')) return 10;
  if (spent.includes('spellcraft_1')) return 5;
  return 0;
}

export function applyInscriptionDiscount(basePrice, state) {
  const discount = getInscriptionDiscountPercent(state);
  if (!discount) return basePrice;
  return Math.max(0, Math.floor(basePrice * (100 - discount) / 100));
}

export function canCraftUniques(state) {
  return Boolean(state?.pointsSpent?.includes('spellcraft_5'));
}

export function canDisenchant(state) {
  return Boolean(state?.pointsSpent?.includes('spellcraft_6'));
}

// ── Battle Mage (scholar path) effective benefits ─────────────────
// Mirrors valkenhall-server/src/utils/skills.js. Server enforces
// the actual reward bumps; client uses these for previews and to
// decide how many quest slots to offer.

export function getBattleMageMatchXpBonusPercent(state) {
  const spent = state?.pointsSpent || [];
  if (spent.includes('scholar_3')) return 20;
  if (spent.includes('scholar_1')) return 10;
  return 0;
}

export function getBattleMageTrialRewardBonusPercent(state) {
  const spent = state?.pointsSpent || [];
  if (spent.includes('scholar_4')) return 20;
  if (spent.includes('scholar_2')) return 10;
  return 0;
}

export function getMaxQuestSlots(state) {
  return state?.pointsSpent?.includes('scholar_5') ? 6 : 3;
}

export function canArchmage(state) {
  return Boolean(state?.pointsSpent?.includes('scholar_6'));
}

// ── Augmentation (hunter path) effective benefits ─────────────────
// Mirrors valkenhall-server/src/utils/skills.js. Server enforces the
// per-card foil rolls during pack generation; the client uses these
// helpers purely to preview affinity rate and capstone status in UI.

export const FOIL_RESONANCE_CHANCE = 0.10;

export function getFoilAffinityBonus(state) {
  const spent = state?.pointsSpent || [];
  if (spent.includes('hunter_5')) return 0.25;
  if (spent.includes('hunter_4')) return 0.20;
  if (spent.includes('hunter_3')) return 0.15;
  if (spent.includes('hunter_2')) return 0.10;
  if (spent.includes('hunter_1')) return 0.05;
  return 0;
}

export function hasFoilResonance(state) {
  return Boolean(state?.pointsSpent?.includes('hunter_6'));
}

// Returns the player's chosen Augmentation element ('air' | 'earth' |
// 'fire' | 'water'), or null if not yet picked.
export function getHunterElement(state) {
  return typeof state?.hunterElement === 'string' ? state.hunterElement : null;
}

// ── Transmutation (transmuter path) effective benefits ────────────
// Mirrors valkenhall-server/src/utils/skills.js. Server enforces the
// actual roll bumps and re-roll bookkeeping; client uses these to
// decide which cards display a re-roll badge during the pack reveal
// and whether the Transmute capstone toggle is available.

export function getLuckyCrackBonusPercent(state) {
  return state?.pointsSpent?.includes('transmuter_1') ? 5 : 0;
}

const REROLL_NODE_BY_RARITY = {
  Ordinary:    'transmuter_2',
  Exceptional: 'transmuter_3',
  Elite:       'transmuter_4',
  Unique:      'transmuter_5',
};

export function canRerollRarity(state, rarity) {
  const node = REROLL_NODE_BY_RARITY[rarity];
  if (!node) return false;
  return Boolean(state?.pointsSpent?.includes(node));
}

export function canTransmute(state) {
  return Boolean(state?.pointsSpent?.includes('transmuter_6'));
}

const RARITY_NEXT_TIER = {
  Ordinary:    'Exceptional',
  Exceptional: 'Elite',
  Elite:       'Unique',
};

export function getNextRarityTier(rarity) {
  return RARITY_NEXT_TIER[rarity] || null;
}
